/**
 * One live connection per browser: tabs elect a leader over a BroadcastChannel, the leader holds the
 * EventSource and rebroadcasts what it receives, followers only listen.
 *
 *   - A new tab says `hello` and waits briefly. A leader answers with a heartbeat at once, so the tab
 *     becomes a follower; silence means it claims leadership itself.
 *   - The leader sends a heartbeat every second (it carries its state). A follower that hears nothing
 *     for DEAD_MS (crashed, frozen or discarded leader) takes over after a short random delay.
 *   - A leader that closes says `bye`; followers claim at once, without waiting for the watchdog.
 *   - Two claims at the same time are settled by every tab with the same rule (the later claim wins,
 *     the id breaks ties), so the loser steps down after one message.
 *   - Without BroadcastChannel every tab is its own leader (one connection per tab).
 */

export const CHANNEL_NAME = "plannello-live";
const HEARTBEAT_MS = 1000;
const DEAD_MS = 3500;
const JOIN_WAIT_MS = 300;

type Wire =
  | { k: "hello"; from: string }
  | { k: "claim"; from: string; at: number }
  | { k: "hb"; from: string; at: number; state: unknown }
  | { k: "bye"; from: string }
  | { k: "data"; from: string; data: unknown };

export interface ElectionHandlers {
  /** This tab became the leader: open the connection. */
  onLead(): void;
  /** This tab is no longer the leader: close the connection. */
  onStepDown(): void;
  /** Something the leader broadcast (events, resets) or its heartbeat state. */
  onData(data: unknown): void;
  onLeaderState(state: unknown): void;
  /** The state a leader puts in its heartbeats. */
  heartbeatState(): unknown;
}

const randomId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export class Election {
  readonly id = randomId();
  private channel: BroadcastChannel | null = null;
  private leader = false;
  private claimedAt = 0;
  private lastHeard = 0;
  private timers = new Set<number>();
  private stopped = false;
  private claiming = false;

  constructor(
    private readonly h: ElectionHandlers,
    private readonly name = CHANNEL_NAME,
  ) {}

  get isLeader() {
    return this.leader;
  }

  start() {
    if (typeof BroadcastChannel === "undefined") {
      this.becomeLeader();
      return;
    }
    this.channel = new BroadcastChannel(this.name);
    this.channel.onmessage = (e: MessageEvent<Wire>) => this.receive(e.data);
    this.lastHeard = 0;
    this.post({ k: "hello", from: this.id });
    this.later(() => this.claimIfUnopposed(), JOIN_WAIT_MS + Math.random() * 120);
    this.every(() => this.tick(), HEARTBEAT_MS);
    // A page restored from the back/forward cache comes back without its timers' history.
    window.addEventListener("pagehide", this.onHide);
    window.addEventListener("pageshow", this.onShow);
  }

  stop() {
    this.stopped = true;
    this.onHide();
    window.removeEventListener("pagehide", this.onHide);
    window.removeEventListener("pageshow", this.onShow);
    for (const t of this.timers) {
      window.clearTimeout(t);
      window.clearInterval(t);
    }
    this.timers.clear();
    this.channel?.close();
    this.channel = null;
    if (this.leader) {
      this.leader = false;
      this.h.onStepDown();
    }
  }

  /** Leader to followers. */
  broadcast(data: unknown) {
    if (this.leader) this.post({ k: "data", from: this.id, data });
  }

  /** Sends a heartbeat right away (the leader's state changed). */
  pulse() {
    if (this.leader) this.post({ k: "hb", from: this.id, at: this.claimedAt, state: this.h.heartbeatState() });
  }

  private onHide = () => {
    if (this.leader) this.post({ k: "bye", from: this.id });
  };

  private onShow = (e: PageTransitionEvent) => {
    if (!e.persisted || this.stopped) return;
    // Back from the cache: whoever led in the meantime is authoritative; ask again.
    if (this.leader) {
      this.leader = false;
      this.h.onStepDown();
    }
    this.lastHeard = 0;
    this.post({ k: "hello", from: this.id });
    this.later(() => this.claimIfUnopposed(), JOIN_WAIT_MS + Math.random() * 120);
  };

  private post(msg: Wire) {
    try {
      this.channel?.postMessage(msg);
    } catch {
      // channel closed
    }
  }

  private later(fn: () => void, ms: number) {
    const t = window.setTimeout(() => {
      this.timers.delete(t);
      if (!this.stopped) fn();
    }, ms);
    this.timers.add(t);
  }

  private every(fn: () => void, ms: number) {
    this.timers.add(window.setInterval(fn, ms));
  }

  private tick() {
    if (this.leader) {
      this.pulse();
    } else if (!this.claiming && this.lastHeard > 0 && Date.now() - this.lastHeard > DEAD_MS) {
      this.claimSoon(Math.random() * 300);
    }
  }

  private claimSoon(delay: number) {
    if (this.claiming || this.leader) return;
    this.claiming = true;
    this.later(() => this.claimIfUnopposed(), delay);
  }

  private claimIfUnopposed() {
    this.claiming = false;
    if (this.leader) return;
    // Someone answered since: stay a follower.
    if (this.lastHeard > 0 && Date.now() - this.lastHeard < DEAD_MS) return;
    this.becomeLeader();
  }

  private becomeLeader() {
    if (this.leader) return;
    this.leader = true;
    this.claimedAt = Date.now();
    this.post({ k: "claim", from: this.id, at: this.claimedAt });
    this.h.onLead();
    this.pulse();
  }

  private stepDown() {
    if (!this.leader) return;
    this.leader = false;
    this.h.onStepDown();
  }

  /**
   * True when the other claimant outranks this tab: the later claim wins (a claim means the previous
   * leader looked dead, e.g. a background tab whose timers are throttled), the id breaks ties.
   */
  private outranked(otherAt: number, otherId: string) {
    return otherAt > this.claimedAt || (otherAt === this.claimedAt && otherId > this.id);
  }

  private receive(msg: Wire) {
    if (this.stopped || !msg || msg.from === this.id) return;
    switch (msg.k) {
      case "hello":
        if (this.leader) this.pulse();
        return;
      case "claim":
      case "hb":
        if (this.leader) {
          if (this.outranked(msg.at, msg.from)) this.stepDown();
          else return; // this tab outranks the sender; the sender steps down when it hears us
        }
        this.lastHeard = Date.now();
        if (msg.k === "hb") this.h.onLeaderState(msg.state);
        return;
      case "bye":
        if (this.leader) return;
        this.lastHeard = 0;
        this.claiming = false;
        this.claimSoon(0);
        return;
      case "data":
        if (this.leader) return;
        this.lastHeard = Date.now();
        this.h.onData(msg.data);
        return;
    }
  }
}
